import { useEffect, useRef } from 'react';

// Create a simple event bus
const eventBus = {
  listeners: {},
  
  // Subscribe to an event
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    };
  },
  
  // Emit an event
  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }
};

// Focus event constants
export const FOCUS_EVENTS = {
  FOCUS_NEW_ITEM: 'focus_new_item',
  FOCUS_HOTKEY_ITEM: 'focus_hotkey_item',
};

// Hook to listen for focus events
export const useFocusEvent = (itemId, callback) => {
  useEffect(() => {
    // Subscribe to the focus event for this specific item
    const unsubscribe = eventBus.on(`${FOCUS_EVENTS.FOCUS_NEW_ITEM}_${itemId}`, callback);
    
    // Clean up subscription
    return unsubscribe;
  }, [itemId, callback]);
};

// Hook to listen for hotkey-created item focus events
export const useHotkeyFocusEvent = (itemId, isHotKeyCreated) => {
  // Use a ref to track if we've already focused
  const hasFocusedRef = useRef(false);
  
  // Reset the focus flag when the hotkey status changes
  useEffect(() => {
    if (isHotKeyCreated) {
      hasFocusedRef.current = false;
    }
  }, [isHotKeyCreated]);
  
  // Effect to focus the input after render
  useEffect(() => {
    // Only proceed if this is a hotkey-created item and we haven't focused yet
    if (isHotKeyCreated && !hasFocusedRef.current) {
      // Mark that we've attempted to focus
      hasFocusedRef.current = true;
      
      // Use requestAnimationFrame to ensure DOM is painted
      requestAnimationFrame(() => {
        const inputElement = document.querySelector(`#style-search-${itemId} input`);
        
        if (inputElement) {
          inputElement.focus();
          inputElement.select(); // Select all text
        }
      });
    }
  }, [isHotKeyCreated, itemId]);
};

// Function to trigger a focus event
export const triggerFocusEvent = (itemId) => {
  // Add a small delay to ensure the component is mounted
  setTimeout(() => {
    eventBus.emit(`${FOCUS_EVENTS.FOCUS_NEW_ITEM}_${itemId}`, {});
  }, 50);
};

export default useFocusEvent; 